/**
 * WebSocket Service
 * Simplified WebSocket service for Django Channels (native WebSocket with JSON messages)
 *
 * Features:
 * - Singleton pattern for global state management
 * - Automatic reconnection with exponential backoff
 * - Handler-based message routing
 * - Support for both legacy (message-based) and token (header-based) authentication
 * - Event emitter pattern for connection lifecycle
 */

import { getUserTokenCookie } from "@/utils/cookies";
import type { BaseHandler } from "./handlers/base.handler";
import type { WebSocketMessage, WebSocketResponse } from "@/types";
import { currencySymbolFirstDeep } from "@/utils/format-money";

// Constants
const WIDGET_NAMESPACE = "unibo-widget:websocket";
const CLOSE_CODE_AUTH_FAILED = 4001;
const CLOSE_CODE_NORMAL = 1000;
// Keep retrying for ~3 minutes before giving up: attempts 1–5 ramp 1s→16s, then
// the delay caps at 30s, so 12 attempts span ~3 min (≈2–4 min depending on jitter).
const MAX_RECONNECT_ATTEMPTS = 12;
const INITIAL_RECONNECT_DELAY = 1000; // ms
const MAX_RECONNECT_DELAY = 30000; // ms

export type ConnectionStatus =
  | "disconnected"
  | "connecting"
  | "connected"
  | "error";

export interface WebSocketServiceEvents {
  connect: () => void;
  disconnect: (code?: number, reason?: string) => void;
  error: (error: Error) => void;
  message: (message: WebSocketResponse) => void;
  /** Every reconnect attempt failed: nothing more is tried until the next `connect()`. */
  giveUp: (attempts: number) => void;
}

type EventCallback = (...args: unknown[]) => void;

export class WebSocketService {
  // Singleton instance
  private static instance: WebSocketService | null = null;

  // Connection state
  private ws: WebSocket | null = null;
  private url: string | null = null;
  private jwtToken: string | null = null;
  private shouldReconnect = true;

  // Reconnection state
  private reconnectAttempts = 0;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;

  // Handlers and events
  private handlers: Map<string, BaseHandler> = new Map();
  private eventListeners: Map<string, Set<EventCallback>> = new Map();

  private constructor() {
    // Private constructor for singleton
  }
  /**
   * Get singleton instance
   */
  static getInstance(): WebSocketService {
    if (!WebSocketService.instance) {
      WebSocketService.instance = new WebSocketService();
    }
    return WebSocketService.instance;
  }

  connect(url: string): void {
    if (this.isConnected()) {
      this.log("Already connected");
      return;
    }

    if (this.isConnecting()) {
      this.log("Connection already in progress");
      return;
    }

    // Close any existing socket, but keep the reconnect bookkeeping intact — a
    // reconnect runs through connect(), and resetting reconnectAttempts here would
    // pin the backoff at attempt 0 (retrying ~every second forever).
    this.closeSocket();

    this.jwtToken = getUserTokenCookie() || null;
    // Maintain this connection: if it later drops unexpectedly, reconnect (with
    // backoff). Previously this was left `false`, so the widget never reconnected.
    this.shouldReconnect = true;
    this.url = url;

    try {
      const userToken = this.jwtToken || "";

      // Use the JWT authentication flow else legacy
      if (userToken) {
        this.ws = new WebSocket(url, ["Bearer", userToken]);
      } else {
        this.ws = new WebSocket(url);
      }
      this.setupHandlers();
    } catch (error) {
      this.logError("Connection error", error);
      this.emit("error", error as Error);
    }
  }

  /**
   * Disconnect WebSocket
   */
  disconnect(): void {
    // Intentional disconnect: stop reconnecting and reset the backoff for next time.
    this.shouldReconnect = false;
    this.reconnectAttempts = 0;
    this.closeSocket();
  }

  /**
   * Close and discard the current socket without touching reconnect state.
   */
  private closeSocket(): void {
    this.clearReconnectTimer();

    if (this.ws) {
      this.ws.close(CLOSE_CODE_NORMAL, "Intentional disconnect");
      this.ws = null;
    }
  }

  /**
   * Setup WebSocket event handlers
   */
  private setupHandlers(): void {
    if (!this.ws) return;

    this.ws.onopen = () => this.handleOpen();
    this.ws.onclose = (event) => this.handleClose(event);
    this.ws.onerror = (error) => this.handleError(error);
    this.ws.onmessage = (event) => this.handleMessage(event);
  }

  /**
   * Handle WebSocket open event
   */
  private handleOpen(): void {
    this.log("WebSocket connected - handshake complete");
    this.reconnectAttempts = 0;
    this.emit("connect");
  }

  /**
   * Handle WebSocket close event
   */
  private handleClose(event: CloseEvent): void {
    this.log(
      `WebSocket closed: code=${event.code}, reason=${event.reason || "none"}`
    );
    this.ws = null;
    this.emit("disconnect", event.code, event.reason);

    // Handle authentication rejection
    if (event.code === CLOSE_CODE_AUTH_FAILED) {
      this.logError("Authentication failed", null);
      this.emit("error", new Error("Authentication failed"));
      this.shouldReconnect = false;
      return;
    }

    // Attempt reconnection if needed
    if (this.shouldReconnect && event.code !== CLOSE_CODE_NORMAL) {
      this.attemptReconnect();
    }
  }

  /**
   * Handle WebSocket error event
   */
  private handleError(error: Event): void {
    this.logError("WebSocket error", error);
    this.log(`WebSocket error - readyState: ${this.ws?.readyState}`);
    this.emit("error", new Error("WebSocket connection error"));
  }

  /**
   * Handle WebSocket message event
   */
  private handleMessage(event: MessageEvent): void {
    try {
      // Every money figure on the wire is server-formatted for the viewer's language; the widget
      // then shows the symbol first regardless ("€2 500,00"), one convention on every screen.
      const message: WebSocketResponse = currencySymbolFirstDeep(
        JSON.parse(event.data)
      );
      void this.processMessageAsync(message);
    } catch (error) {
      this.logError("Failed to parse message", error);
    }
  }

  // ============================================================================
  // Message Processing
  // ============================================================================

  /**
   * Process incoming message from Django Channels (async so handler rejections are caught)
   */
  private async processMessageAsync(message: WebSocketResponse): Promise<void> {
    await this.routeToHandlersAsync(message);
  }

  /**
   * Route message to appropriate handlers
   */
  private async routeToHandlersAsync(
    message: WebSocketResponse
  ): Promise<void> {
    if (!message.type) {
      return;
    }

    const handler = this.handlers.get(message.type);
    if (handler) {
      await this.invokeHandlerAsync(handler, message);
    }
  }

  /**
   * Safely invoke a handler (awaits async handle so rejections don't go unhandled)
   */
  private async invokeHandlerAsync(
    handler: BaseHandler,
    message: WebSocketResponse
  ): Promise<void> {
    try {
      await handler.handle(message);
    } catch (error) {
      this.logError(`Handler error for ${handler.constructor.name}`, error);
    }
  }

  // ============================================================================
  // Message Sending
  // ============================================================================

  /**
   * Send message to Django Channels
   * @param type - Message type
   * @param payload - Message payload
   */
  /** Returns whether the message was actually handed to the socket. `false` when
   *  not connected (mid-reconnect) — callers that track in-flight requests must
   *  not enqueue on a `false`, or a phantom (never-acked) entry results. */
  send(type: string, payload: Record<string, unknown> = {}): boolean {
    if (!this.isConnected()) {
      this.log(`Cannot send message (not connected): ${type}`);
      return false;
    }

    const message: WebSocketMessage = {
      type,
      ...payload,
    };

    this.ws!.send(JSON.stringify(message));
    return true;
  }

  // ============================================================================
  // Reconnection Logic
  // ============================================================================

  /**
   * Attempt to reconnect if conditions are met
   */
  private attemptReconnect(): void {
    if (this.reconnectAttempts >= MAX_RECONNECT_ATTEMPTS) {
      this.log(`Max reconnection attempts (${MAX_RECONNECT_ATTEMPTS}) reached`);
      this.emit("giveUp", this.reconnectAttempts);
      return;
    }

    this.scheduleReconnect();
  }

  /**
   * Schedule reconnection attempt with exponential backoff
   */
  private scheduleReconnect(): void {
    this.clearReconnectTimer();

    this.reconnectAttempts++;
    const delay = this.calculateReconnectDelay();

    this.log(
      `Reconnecting in ${delay}ms (attempt ${this.reconnectAttempts}/${MAX_RECONNECT_ATTEMPTS})`
    );

    this.reconnectTimer = setTimeout(() => {
      if (this.url && this.shouldReconnect) {
        this.connect(this.url);
      }
    }, delay);
  }

  /**
   * Calculate reconnection delay with exponential backoff
   */
  private calculateReconnectDelay(): number {
    const backoff = Math.min(
      INITIAL_RECONNECT_DELAY * Math.pow(2, this.reconnectAttempts - 1),
      MAX_RECONNECT_DELAY
    );
    // Equal jitter so clients don't reconnect in lockstep after a mass disconnect.
    return Math.round(backoff / 2 + Math.random() * (backoff / 2));
  }

  /**
   * Clear reconnection timer
   */
  private clearReconnectTimer(): void {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }

  // ============================================================================
  // Status and State Getters
  // ============================================================================

  /**
   * Get connection status
   */
  getStatus(): ConnectionStatus {
    if (!this.ws) return "disconnected";

    switch (this.ws.readyState) {
      case WebSocket.CONNECTING:
        return "connecting";
      case WebSocket.OPEN:
        return "connected";
      case WebSocket.CLOSING:
      case WebSocket.CLOSED:
        return "disconnected";
      default:
        return "error";
    }
  }

  /**
   * Check if WebSocket is connected
   */
  private isConnected(): boolean {
    return this.ws?.readyState === WebSocket.OPEN;
  }

  /**
   * Check if WebSocket is connecting
   */
  private isConnecting(): boolean {
    return this.ws?.readyState === WebSocket.CONNECTING;
  }

  // ============================================================================
  // Handler Management
  // ============================================================================

  /**
   * Register message handler
   * @param type - Message type to handle
   * @param handler - Handler instance
   */
  registerHandler(type: string, handler: BaseHandler): void {
    this.handlers.set(type, handler);
  }

  /**
   * Unregister message handler
   * @param type - Message type
   */
  unregisterHandler(type: string): void {
    this.handlers.delete(type);
    this.log(`Unregistered handler for type: ${type}`);
  }

  // ============================================================================
  // Event Management
  // ============================================================================

  /**
   * Register event listener
   */
  on<K extends keyof WebSocketServiceEvents>(
    event: K,
    callback: WebSocketServiceEvents[K]
  ): void {
    if (!this.eventListeners.has(event)) {
      this.eventListeners.set(event, new Set());
    }
    this.eventListeners.get(event)!.add(callback as EventCallback);
  }

  /**
   * Unregister event listener
   */
  off<K extends keyof WebSocketServiceEvents>(
    event: K,
    callback: WebSocketServiceEvents[K]
  ): void {
    this.eventListeners.get(event)?.delete(callback as EventCallback);
  }

  /**
   * Emit event to all listeners
   */
  private emit<K extends keyof WebSocketServiceEvents>(
    event: K,
    ...args: Parameters<WebSocketServiceEvents[K]>
  ): void {
    this.eventListeners.get(event)?.forEach((callback) => {
      try {
        (callback as (...args: unknown[]) => void)(...args);
      } catch (error) {
        this.logError(`Error in event listener for ${event}`, error);
      }
    });
  }

  // ============================================================================
  // Logging
  // ============================================================================

  /**
   * Log message
   */
  private log(message: string, data?: unknown): void {
    console.log(`[${WIDGET_NAMESPACE}] ${message}`, data || "");
  }

  /**
   * Log error
   */
  private logError(message: string, error: unknown): void {
    console.error(`[${WIDGET_NAMESPACE}] ${message}`, error || "");
  }
}
